import { Stack } from '@mui/material'

import { Artist, Maybe, PostBoxFragment } from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { PostBox, PostBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import {
  SearchForm,
  SearchFormProps,
} from '@/components/pages/ConsolePosts/template/SearchForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  userId: string
  artist: Maybe<Pick<Artist, 'id' | 'paymentType'>>
  loading: boolean
  hasNextPage: boolean
  loadingDeletePost: boolean
  posts: Maybe<{ edges: { node: PostBoxFragment }[] }>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFilter: SearchFormProps['onSubmit']
  onFetchMore: () => Promise<void>
  onDeletePost: (id: string) => Promise<void>
}

export const ConsoleUnpublishedPostsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onDeletePost,
  onFilter,
}) => {
  const { t } = useTranslation('consoleUnpublishedPosts')
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleUnpublishedPosts} />
      <ConsoleBaseLayout
        hasConsoleFooter
        header={{
          back: true,
          defaultBackPage: routerPages.ConsolePost,
          title: t('title'),
        }}
      >
        <SearchForm
          hiddenFilter={!state.posts?.edges.length}
          onSubmit={onFilter}
        />
        <Stack sx={{ px: 5, pt: 2 }}>
          <ScrollFetcher
            nodes={state.posts?.edges.map(({ node }) => (
              <PostBox
                loadingDeletePost={state.loadingDeletePost}
                onDeletePost={onDeletePost}
                post={node}
                fullWidth
                isConsole
                userId={state.userId}
              />
            ))}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={onFetchMore}
            emptyMessage={t('emptyPost')}
            loadingComponent={
              <Stack spacing={3} sx={{ pb: 2 }}>
                {Array.from(Array(3).keys()).map(key => (
                  <PostBoxSkeleton fullWidth key={key} />
                ))}
              </Stack>
            }
          />
        </Stack>
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}
